import { apiService } from "./api";
import { RequestSourcesSummary } from "./guestOrderService";

// Shop Analytics — see api/app/services/shop_analytics.py for exactly what
// each figure counts. Periods are whole days (YYYY-MM-DD), Dhaka time.

export type Bucket = "day" | "week" | "month";

export interface ShopOnline {
    date_from: string;
    date_to: string;
    bucket: Bucket;
    placed: number;
    approved: number;
    rejected: number;
    waiting: number;
    approval_rate: number | null;
    median_review_hours: number | null;
    approved_value: number;
    average_approved_value: number | null;
    online_share: {
        orders: number;
        all_orders: number;
        percent: number | null;
        value: number;
        all_value: number;
        value_percent: number | null;
    };
    bkash: {
        attempts: number;
        paid: number;
        failed: number;
        abandoned: number;
        in_progress: number;
        success_rate: number | null;
    };
    payment_methods: { method: string; count: number }[];
    rejection_reasons: { reason: string; count: number }[];
    trend: { bucket: string; placed: number; approved: number }[];
    sources: Omit<RequestSourcesSummary, "days">;
}

export interface CustomerGroup {
    customers: number;
    orders: number;
    value: number;
}

export interface ShopCustomers {
    date_from: string;
    date_to: string;
    active_customers: number;
    new: CustomerGroup;
    returning: CustomerGroup;
    repeat_rate: number | null;
    median_days_to_second_order: number | null;
    orders_without_customer: number;
    cohorts: {
        month: string; // YYYY-MM
        customers: number;
        retained: (number | null)[];
        rates: (number | null)[];
    }[];
    delivery_areas: { area: string; requests: number; approved_value: number }[];
    signups: number;
    pets: { owners: number; species: { species: string; count: number }[] };
}

export interface ShopChannels {
    date_from: string;
    date_to: string;
    bucket: Bucket;
    channels: { name: string; orders: number; value: number; share: number | null; order_share: number | null }[];
    series: { bucket: string; values: Record<string, number>; orders: Record<string, number> }[];
    total_orders: number;
    total_value: number;
    not_set_order_share: number;
}

const BASE = "/reports/shop";

const query = (dateFrom: string, dateTo: string, fresh: boolean) =>
    `?date_from=${dateFrom}&date_to=${dateTo}${fresh ? "&fresh=1" : ""}`;

class ShopAnalyticsService {
    getOnline(dateFrom: string, dateTo: string, fresh = false): Promise<ShopOnline> {
        return apiService.get<ShopOnline>(`${BASE}/online${query(dateFrom, dateTo, fresh)}`);
    }

    getCustomers(dateFrom: string, dateTo: string, fresh = false): Promise<ShopCustomers> {
        return apiService.get<ShopCustomers>(`${BASE}/customers${query(dateFrom, dateTo, fresh)}`);
    }

    getChannels(dateFrom: string, dateTo: string, fresh = false): Promise<ShopChannels> {
        return apiService.get<ShopChannels>(`${BASE}/channels${query(dateFrom, dateTo, fresh)}`);
    }
}

export const shopAnalyticsService = new ShopAnalyticsService();
